import { defineStore } from "pinia";
import type {
  CatalogRepairBatchResult,
  CatalogRepairJobProgressDto,
} from "@price-rescue/api-contracts";
import { CATALOG_REPAIR_OUTCOMES } from "@price-rescue/domain/catalog-repair";
import { JOB_STATUS_QUEUED, JOB_STATUS_RUNNING } from "@price-rescue/domain/statuses";
import { MerchantApiError } from "../authentication/errors.js";
import { freezeProductBulkSelection } from "./product-bulk-selection.js";
import type { CatalogRepairService } from "./catalog-repair.js";

export const useCatalogRepairStore = defineStore("product-catalog-repair", {
  state: (): {
    service?: CatalogRepairService;
    selection: string[];
    result?: CatalogRepairBatchResult;
    jobs: Record<string, CatalogRepairJobProgressDto>;
    error?: string;
    submitting: boolean;
    needsRefresh: boolean;
    pendingRequest?: { productIds: string[]; idempotencyKey: string };
  } => ({ selection: [], jobs: {}, submitting: false, needsRefresh: false }),
  actions: {
    configure(service: CatalogRepairService): void {
      this.service = service;
    },
    prepare(productIds: string[]): void {
      if (this.submitting || this.needsRefresh) return;
      try {
        const selection = freezeProductBulkSelection(productIds);
        this.selection = selection;
        this.jobs = {};
        delete this.result;
        delete this.error;
      } catch (error: unknown) {
        this.selection = [];
        this.error = error instanceof Error ? error.message : "Review the selected products.";
      }
    },
    async execute(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting || this.needsRefresh || !this.selection.length) return;
      const service = this.service;
      if (!service) {
        this.error = "Catalog repair is unavailable.";
        return;
      }
      this.submitting = true;
      delete this.error;
      this.pendingRequest = {
        productIds: [...this.selection],
        idempotencyKey: globalThis.crypto.randomUUID(),
      };
      try {
        await this.reconcileStart(service);
        this.needsRefresh = true;
        await this.refreshProducts(refresh);
      } catch (error: unknown) {
        this.error = error instanceof Error ? error.message : "Catalog repair could not start.";
        if (
          !(error instanceof MerchantApiError) ||
          ["network", "server", "unknown"].includes(error.kind)
        ) {
          this.result = {
            queued: [],
            existing: [],
            failed: [],
            skipped: [],
            unconfirmed: this.selection.map((productId) => ({
              productId,
              code: CATALOG_REPAIR_OUTCOMES.UNCONFIRMED,
              message:
                "A repair may have started. Refresh and review before requesting another batch.",
            })),
          };
          this.needsRefresh = true;
        } else {
          delete this.pendingRequest;
        }
      } finally {
        this.submitting = false;
      }
    },
    async refreshProgress(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting) return;
      this.submitting = true;
      delete this.error;
      try {
        const service = this.service;
        if (this.pendingRequest) {
          if (!service) throw new Error("Catalog repair reconciliation is unavailable.");
          await this.reconcileStart(service);
        }
        const failed = service
          ? await this.refreshActiveJobs(service)
          : Object.keys(this.jobs).length > 0;
        await this.refreshProducts(refresh);
        if (failed && !this.needsRefresh)
          this.error =
            "Some repair statuses could not be refreshed. Last reported results remain visible.";
      } catch {
        this.needsRefresh = true;
        this.error =
          "The original repair is still unconfirmed. Refresh repair progress to reconcile it before another batch.";
      } finally {
        this.submitting = false;
      }
    },
    async reconcileStart(service: CatalogRepairService): Promise<void> {
      const pending = this.pendingRequest;
      if (!pending) return;
      const result = await service.start([...pending.productIds], pending.idempotencyKey);
      this.result = result;
      for (const item of [...result.queued, ...result.existing])
        this.jobs[item.job.jobId] = item.job;
      if (!result.unconfirmed.length) delete this.pendingRequest;
    },
    async refreshActiveJobs(service: CatalogRepairService): Promise<boolean> {
      const active = Object.values(this.jobs).filter(
        (job) => job.status === JOB_STATUS_QUEUED || job.status === JOB_STATUS_RUNNING,
      );
      let failed = false;
      // Bound requests without making a 100-product batch wait for 100 serial round trips.
      for (let offset = 0; offset < active.length; offset += 4) {
        await Promise.all(
          active.slice(offset, offset + 4).map(async (job) => {
            try {
              this.jobs[job.jobId] = await service.getJob(job.jobId);
            } catch {
              failed = true;
            }
          }),
        );
      }
      return failed;
    },
    async refreshProducts(refresh: () => Promise<void>): Promise<void> {
      try {
        await refresh();
        this.needsRefresh = this.pendingRequest !== undefined;
        if (this.needsRefresh)
          this.error = "Refresh repair progress to reconcile the original unconfirmed repair.";
      } catch {
        this.needsRefresh = true;
        this.error = "Refresh products and review repair results before another batch.";
      }
    },
  },
});
